import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import useStylesFieldForm from 'app/profile/settings/styles/useStylesFieldForm.styles';
import useStylesSettings from 'app/profile/settings/styles/useStylesSettings.styles';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import JsonCodeInputMonacoEditor from 'common/components/code-editor/JsonCodeInputMonacoEditor';
import SimpleCodeErrorView from 'common/components/code-editor/SimpleCodeErrorView';
import useFlexBoxStyles from 'common/flexBox';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSettingsDirty } from 'redux/slices/profileSlice';
import ItemSettings from '../components/ItemSettings';

const Leads = ({
    enableAddLeadManual,
    enableSendLeadRemarketingEmail,
    enableStartConversationWithAiAgent,
    leadStatus,
    enableUnqualifiedLeadTier,
    unqualifiedLeadTierDays,
    unqualifiedLeadTier,
    toggleChange,
    handleSubmit,
    hasChanges,
}) => {
    const { t } = useTranslation();
    const { classes: classes1 } = useStylesFieldForm();
    const { classes: classes2 } = useStylesSettings();
    const { classes: flexBox } = useFlexBoxStyles();

    const [inputError, setInputError] = useState({ leadStatus: null });
    const classes = { ...classes1, ...classes2 };

    const settingsDirty = useSelector(getSettingsDirty);

    const unqualifiedLeadTierDaysValid = Number.isInteger(+unqualifiedLeadTierDays) && +unqualifiedLeadTierDays > 0;
    const unqualifiedLeadTierValid = typeof unqualifiedLeadTier === 'string' && unqualifiedLeadTier.trim() !== '';
    // The days and the tier only apply while the unqualified lead tier is enabled
    const unqualifiedLeadTierSettingsValid =
        !enableUnqualifiedLeadTier || (unqualifiedLeadTierDaysValid && unqualifiedLeadTierValid);

    const handleCheckChange = (event, settingName) => {
        const checked = event.target.checked;
        toggleChange(settingName, checked);
    };

    return (
        <Accordion className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ArrowRightIcon className={classes.accordionIcon} />}
                aria-controls="panel2a-content"
                id="panel2a-header"
            >
                <Typography variant="subtitle1" fontWeight={500}>
                    {t('profile.settings.leadsSettings.leads')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.eventSection}>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                    <Box
                        sx={{
                            display:
                                settingsDirty && hasChanges && unqualifiedLeadTierSettingsValid
                                    ? 'inline-flex'
                                    : 'none',
                        }}
                    >
                        <Button variant="contained" className={classes.redColor} size="small" onClick={handleSubmit}>
                            {t('profile.settings.leadsSettings.save')}
                        </Button>
                    </Box>
                </Box>
                <ItemSettings
                    label={t('profile.settings.leadsSettings.enableAddLeadManual')}
                    onChange={handleCheckChange}
                    name={'enableAddLeadManual'}
                    keySettings={SettingsKey.ENABLE_ADD_LEAD_MANUAL}
                    value={enableAddLeadManual}
                    paddingBottom
                />
                <ItemSettings
                    label={t('profile.settings.leadsSettings.enableSendLeadRemarketingEmail')}
                    onChange={handleCheckChange}
                    name={'enableSendLeadRemarketingEmail'}
                    keySettings={SettingsKey.ENABLE_SEND_LEAD_REMARKETING_EMAIL}
                    value={enableSendLeadRemarketingEmail}
                    paddingBottom
                />
                <Stack className={flexBox.paddingTop2}>
                    <Typography className={classes.emailsTemplatesText}>{`${t(
                        'settings.leads.leadsStatus',
                    )}`}</Typography>
                    <JsonCodeInputMonacoEditor
                        name="leadStatus"
                        value={leadStatus}
                        onChange={(value, errors) => {
                            const codeHaveErrors = errors && Array.isArray(errors) && errors.length > 0;
                            const newErrors = codeHaveErrors ? errors : null;
                            setInputError({ leadStatus: newErrors });
                            toggleChange(SettingsKey.LEADS_STATUS, value);
                        }}
                    />
                    <SimpleCodeErrorView
                        id={'error-context'}
                        mainErrorMessage={t('JsonError')}
                        errorList={inputError['leadStatus']}
                    />
                </Stack>
                <ItemSettings
                    label={t('profile.settings.leadsSettings.enableStartConversationWithAiAgent')}
                    onChange={handleCheckChange}
                    name={'enableStartConversationWithAiAgent'}
                    keySettings={SettingsKey.ENABLE_START_CONVERSATION_WITH_AI_AGENT}
                    value={enableStartConversationWithAiAgent}
                />
                <ItemSettings
                    label={t('profile.settings.leadsSettings.enableUnqualifiedLeadTier')}
                    onChange={handleCheckChange}
                    name={'enableUnqualifiedLeadTier'}
                    keySettings={SettingsKey.ENABLE_UNQUALIFIED_LEAD_TIER}
                    value={enableUnqualifiedLeadTier}
                />
                {enableUnqualifiedLeadTier && (
                    <Stack spacing={2} className={flexBox.paddingTop2} sx={{ width: '60%' }}>
                        <Typography className={classes.text}>
                            {t('profile.settings.leadsSettings.unqualifiedLeadTierDescription')}
                        </Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            type="number"
                            label={t('profile.settings.leadsSettings.unqualifiedLeadTierDays')}
                            InputProps={{ inputProps: { min: 1, step: 1 } }}
                            value={+unqualifiedLeadTierDays}
                            onChange={(e) => toggleChange(SettingsKey.UNQUALIFIED_LEAD_TIER_DAYS, +e.target.value)}
                            error={!unqualifiedLeadTierDaysValid}
                            helperText={
                                !unqualifiedLeadTierDaysValid &&
                                t('profile.settings.leadsSettings.unqualifiedLeadTierDaysError')
                            }
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            label={t('profile.settings.leadsSettings.unqualifiedLeadTier')}
                            value={unqualifiedLeadTier ?? ''}
                            onChange={(e) => toggleChange(SettingsKey.UNQUALIFIED_LEAD_TIER, e.target.value)}
                            error={!unqualifiedLeadTierValid}
                            helperText={
                                !unqualifiedLeadTierValid &&
                                t('profile.settings.leadsSettings.unqualifiedLeadTierError')
                            }
                        />
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default Leads;
